import { Component, Element, Host, Prop, State, Watch, Listen, h } from '@stencil/core';
import iconSizes from '../icon/_icon-sizes';
import debounce from '../../capabilities/debounce';
import uid from '../../capabilities/identity/uid';
import warn from '../../capabilities/warn';

/**
 * hp6-text-select
 *
 * @description The text-select decorates a <select />, applies hp-themes styling, and changes the width of the select as the selected option changes
 * @use
 * @since 5.11.0
 * @slot - `default slot`: The insertion of the select element.
 * @version figma-v1
 */
@Component({
  tag: 'hp6-text-select',
  styleUrl: 'text-select.scss',
  // shadow: true, // label not allowed inside shadow DOM
  shadow: false,
})
export class TextSelect {
  /**
   * The label for this component. REQUIRED - Renders null if not supplied.
   */
  @Prop() labelText: string;

  /**
   * The size of the label text. "xsmall Heading" or "small Heading"
   * - xxsmall heading label uses xsmall body for selection options
   * - xsmall heading label uses small body for selection options
   * - small heading label uses medium body for selection options
   * - medium heading label uses large body for selection options
   */
  @Prop() labelSizeSelection: 'xxs' | 'xs' | 'sm' | 'md' = 'xs';

  /**
   * Will be the aria-controls="" arribute of the <select />
   */
  @Prop() allyControls: string = null;

  /**
   * ref to the <Host /> element
   */
  @Element() host: HTMLElement;

  /**
   * The read/write selected index of the wrapped select.
   * Use this prop to change selectElement.selectedOption
   */
  @Prop({ mutable: true, reflect: true }) selectedIndex: number;

  /**
   * Width of the buffer element
   */
  @State() bufferWidth: number;

  /**
   * Width of the select element
   */
  @State() selectElementWidth: number;

  /**
   * Returns the select element from the <slot />
   */
  get selectElement(): HTMLSelectElement {
    const { host } = this;
    const selectElement = host.querySelector('select');
    return selectElement;
  }

  /**
   * An array of pixel widths of each of the select options.
   */
  private optionWidths: number[] = [];

  /**
   * An array of CSS classes that may have been on this.selectElement in the <slot /> when
   * this component was first rendered.
   */
  private classListInitial: string[] = [];

  /**
   * The id of the select element
   * might be hard-coded in the <select /> tag or generated if not hard-coded
   */
  private selectElementId: string = `hp-text-select-id-${uid()}`;

  /**
   * Return value of window.setTimeout()
   */
  private resizeTimeout: number;

  /**
   * handleWindowResize()
   */
  @Listen('resize', { target: 'window' })
  handleWindowResize(): void {
    this.onWindowResize();
  }

  /**
   * onWindowResize()
   */
  onWindowResize(): void {
    this.storeOptionWidths();
  }

  /**
   * labelSizeSelectionChanged()
   *
   * @param newValue
   * @param oldValue
   */
  @Watch('labelSizeSelection')
  labelSizeSelectionChanged(newValue: boolean = undefined, oldValue: boolean = undefined) {
    if (newValue !== oldValue) {
      this.storeOptionWidths();
    }
  }

  @Watch('selectedIndex')
  onSelectedIndexChange() {
    const { selectedIndex } = this;
    const options = this.getSelectOptions();

    if (selectedIndex >= options.length) {
      warn(
        `hp6-text-select -- not setting selectedIndex prop ${selectedIndex} is greater than options.length`,
      );
      return;
    }

    if (selectedIndex < 0) {
      warn(
        `hp6-text-select -- not setting selectedIndex prop to negative number ${selectedIndex} `,
      );
      return;
    }

    const { selectElement } = this;
    selectElement.selectedIndex = selectedIndex;

    this.resizeSelect();
  }

  /**
   * resizeSelect()
   */
  resizeSelect(): void {
    const { host, optionWidths } = this;

    const windowWidth = window.innerWidth;
    const labelWidth = host.querySelector('hp6-label-decorator').clientWidth;

    if (optionWidths.length <= 0) {
      warn('resizeSelect() called without optionWidths set');
      return;
    }

    const { selectElement, labelSizeSelection } = this;

    // remove all classes from the select
    selectElement.className = '';

    // re-add the initial classes onto selectElement
    this.classListInitial.forEach(c => {
      selectElement.classList.add(c);
    });

    selectElement.classList.add('hp6-text-select-element');
    selectElement.classList.add(labelSizeSelection);

    const { selectedIndex } = selectElement;

    const { iconSize } = this;
    const selectedItemWidth = optionWidths[selectedIndex];
    this.selectElementWidth = selectedItemWidth + iconSizes[iconSize];
    // remainingSpace is the space left after the window size, label, icon and page content spacing is accounted for
    const remainingSpace = windowWidth - labelWidth - iconSizes[iconSize] - 33; // 33px is the padding (30px) for the page-container and select padding (3px)

    // resize the entire <select /> width
    this.selectElement.style.width = `${this.selectElementWidth.toString()}px`;
    this.selectElement.style.maxWidth = `${remainingSpace.toString()}px`;
  }

  /**
   * onChange()
   */
  private onChange(): void {
    const { selectElement } = this;
    const { selectedIndex } = selectElement;
    this.selectedIndex = selectedIndex;
  }

  /**
   * getSelectOptions()
   *
   * TODO: determine a way to prevent allowing "multiple" in the <select /> - implement same in <hp6-select />
   *
   * @returns HTMLOptionsCollection
   */
  private getSelectOptions() {
    const { selectElement } = this;
    const { options } = selectElement;
    return options;
  }

  /**
   * storeOptionWidths()
   */
  storeOptionWidths() {
    const { iconSize, labelSizeSelection } = this;

    const hiddenDiv = document.createElement('div');
    hiddenDiv.classList.add('hp6-text-select-element');
    hiddenDiv.classList.add(labelSizeSelection);

    const { host } = this;
    host.appendChild(hiddenDiv);

    const options = this.getSelectOptions();

    let widest = 0;

    const optionWidths = [];

    for (let i = 0; i < options.length; i++) {
      const option = options[i];

      const innerHTML = `${option.label}&nbsp;`;
      hiddenDiv.innerHTML = innerHTML;

      const width = hiddenDiv.clientWidth;
      optionWidths[i] = width;

      if (width > widest) {
        widest = width;
      }
    }

    this.optionWidths = optionWidths;
    host.removeChild(hiddenDiv);

    // icon sizes defined in ../../styles/icon-sizes.scss
    const iconWidth = iconSizes[iconSize];
    this.bufferWidth = widest + iconWidth * 2; // 2x icon width for relative positioning
  }

  /**
   * Stencil lifecycle
   */
  disconnectedCallback() {
    const { selectElement, resizeTimeout } = this;
    selectElement.removeEventListener('change', this.onChange);
    clearTimeout(resizeTimeout);
  }

  /**
   * Stencil lifecycle
   */
  componentWillLoad() {
    const { selectElement, labelSizeSelection } = this;

    // assign an id to the <select /> if one was not hard-coded
    const { id } = selectElement;
    let selectElementId = id;

    if (!selectElementId) {
      selectElementId = this.selectElementId;
    }

    selectElement.id = selectElementId;
    this.selectElementId = selectElementId;

    const { classList } = selectElement;
    this.classListInitial = Array.from(classList);

    selectElement.classList.add('hp6-text-select-element');
    selectElement.classList.add(labelSizeSelection);
    selectElement.addEventListener('change', this.onChange);

    const { selectedIndex } = this;
    selectElement.selectedIndex = selectedIndex;
    this.storeOptionWidths();
  }

  componentDidRender() {
    this.resizeSelect();

    this.resizeTimeout = window.setTimeout(() => {
      const icons = this.host.querySelectorAll('hp6-icon');
      icons.forEach(icon => {
        icon.classList.add('loaded');
      });
    }, 100);
  }

  /**
   * constructor()
   */
  constructor() {
    const t = 100;

    this.getSelectOptions = this.getSelectOptions.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onWindowResize = debounce(this.onWindowResize.bind(this), t);
    this.onSelectedIndexChange = this.onSelectedIndexChange.bind(this);
    this.storeOptionWidths = debounce(this.storeOptionWidths.bind(this), t + 1);
  }

  /**
   * get iconSize()
   */
  get iconSize() {
    const { labelSizeSelection } = this;
    return labelSizeSelection === 'md' ? 'sm' : 'xs';
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const { selectElementId, labelText, labelSizeSelection, iconSize, allyControls } = this;

    if (!labelText) {
      warn('hp6-text-select - labelText attribute required');
      return null;
    }

    let ariaAttributes = {};

    if (allyControls) {
      // TODO: why is allControls not being used?
      // eslint 9 discovered this bug
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ariaAttributes = {
        ...ariaAttributes,
        'aria-controls': allyControls,
      };
    }

    return (
      <Host>
        <div class="text-select-container">
          <hp6-label-decorator
            size-selection={labelSizeSelection}
            type-selection="heading"
            show-field-optional="false"
          >
            <label htmlFor={selectElementId}>{labelText}</label>
          </hp6-label-decorator>

          <div class={`select-and-icon-buffer ${iconSize}`}>
            <div class="select-and-icon-container">
              <slot />
              <hp6-icon name="system-arrow-down" size-selection={iconSize} />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
